import {
  IGetBlockReferenceByIdQuery,
  INoteByIdQuery,
  OverrideSQuery,
} from '@rhizome/pgtypes';
import { DBToSlate } from '@rhizome/shared-slate-utils';
import { OpenInNew, South } from '@styled-icons/material';
import Tippy from '@tippyjs/react';
import { useAuthor } from 'apps/exegesis/utils/useAuthor';
import Link from 'next/link';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { mouseDown } from '../../../pages/_app';
import { useFetcher } from '../../../utils/DataFetchers';
import { usePrivacy } from '../../../utils/usePrivacy';
import BacklinksWrapper from '../../NBacklinksWrapper';
import { LoadingPulser } from '../../StatusUI/LoadingPulser';
import { LoadingSpinner } from '../../StatusUI/LoadingSpinner';
import { ReadOnlyDisplay } from '../usedPlugins';

// it needs the refs passed in because i'm not sure if we can use hooks here?
// is this a react component? who knows?
export const RenderPreview = (
  value: any,
  BacklinksRef: any,
  ScrollContainerRef: any,
  author: any,
  isPublic: boolean
) => {
  return function RenderPreviewComponent(attrs: any) {
    if (mouseDown) {
      return <div></div>;
    }
    return (
      <PreviewBox tabIndex={-1} {...attrs} contentEditable={false}>
        <TopDivider>
          <PreviewHeader>{value}</PreviewHeader>
          <Tippy content="jump to linked content" offset={[0, 0]} arrow={false}>
            <JumpDownButton
              onClick={() => {
                BacklinksRef.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                });
              }}
            >
              <South height="20px" />
            </JumpDownButton>
          </Tippy>
          <Tippy content={`open '${value}'`} offset={[0, 0]} arrow={false}>
            <GoToButton>
              <Link
                passHref
                href={
                  isPublic
                    ? '/user/' + author + '/tag/' + encodeURIComponent(value)
                    : '/notes/tag/' + encodeURIComponent(value)
                }
              >
                <OpenInNew height="20px" />
              </Link>
            </GoToButton>
          </Tippy>
        </TopDivider>
        <ContentWrapper ref={ScrollContainerRef}>
          <NoteDisplay value={value} author={author} public={isPublic} />
          <span ref={BacklinksRef}>
            <BacklinksWrapper
              initialTags={[value]}
              containerRef={ScrollContainerRef}
              public={isPublic}
              open={false}
              isRoot={false}
            />
          </span>
        </ContentWrapper>
      </PreviewBox>
    );
  };
};

export const RenderBlockPreview = (blockId: string) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function BlockPreviewComponent(attrs: any) {
    if (mouseDown) {
      return <div></div>;
    }
    return (
      <div tabIndex={-1} {...attrs} contentEditable={false}>
        <BlockDisplay blockId={blockId} />
      </div>
    );
  };
};

export const BlockDisplay = React.memo(function BlockDisplayComponent(props: {
  blockId: string;
}) {
  const isPublic = usePrivacy() === 'public';
  const { data: block, status: blockStatus } = useFetcher<
    OverrideSQuery<IGetBlockReferenceByIdQuery>
  >(
    [
      'GetBlockReferenceById',
      {
        block_id: props.blockId,
      },
    ],

    {
      skipAuth: isPublic,
    }
  );
  const initialValue = useMemo(() => {
    if (block) {
      const converted = DBToSlate(block);
      return converted;
    }
    return [];
  }, [block]);

  const isPrivate = usePrivacy() === 'private';

  if (blockStatus === 'loading') {
    return (
      <BlockPreviewBox>
        <LoadingPulser />
      </BlockPreviewBox>
    );
  }
  const title = block?.[0]?.parent_title || 'untitled';
  const privateLink =
    '/notes/editor/' + block?.[0]?.slate_parent + '#' + block?.[0]?.block_id;

  const publicLink =
    '/post/' +
    block?.[0]?.slate_parent +
    '/' +
    '?excerpt=' +
    block?.[0]?.block_id;
  return (
    <BlockPreviewBox>
      <TopDivider>
        <PreviewHeader>block from &apos;{title}&apos;</PreviewHeader>
        <Tippy
          content={`go to block in parent note`}
          offset={[0, 0]}
          arrow={false}
        >
          <GoToButton>
            <Link passHref href={isPrivate ? privateLink : publicLink}>
              <OpenInNew height="20px" />
            </Link>
          </GoToButton>
        </Tippy>
      </TopDivider>
      <ContentWrapper>
        <FadeContainer>
          {block && block.length > 0 ? (
            <ReadOnlyDisplay nodes={initialValue} isRoot={false} />
          ) : isPublic ? (
            <h2>This block is private</h2>
          ) : (
            <h2>This block has been deleted</h2>
          )}
        </FadeContainer>
      </ContentWrapper>
    </BlockPreviewBox>
  );
});

const NoteDisplay = React.memo(function NoteDisplayComponent(props: {
  value: string;
  author: string;
  public: boolean;
}) {
  const author = useAuthor((state) => state.author);
  const { data: note, status: noteStatus } = useFetcher<
    OverrideSQuery<INoteByIdQuery>
  >(
    [
      'NoteById',
      {
        note_author: props.author,
        note_title: props.value,
        note_public: props.public || null,
        note_id: null,
      },
    ],
    {}
  );

  const initialValue = useMemo(() => {
    if (note) {
      const converted = DBToSlate(note);
      return converted;
    } else {
      return [];
    }
  }, [note]);
  if (noteStatus === 'loading') {
    return (
      <LoadingSpinner width={50} style={{ marginTop: 'calc(180px - 50px)' }} />
    );
  }
  return (
    <FadeContainer>
      <ReadOnlyDisplay nodes={initialValue} isRoot={false} />
    </FadeContainer>
  );
});

const FadeContainer = styled.span`
  display: inline-block;
  animation-name: fadein;
  animation-duration: 0.5s;
`;

const PreviewBox = styled.span`
  animation-name: fadein;
  animation-duration: 0.2s;

  display: block;
  height: 400px;
  width: 300px;
  border-radius: 0.4rem;
  border: 1px solid black;
  background-color: ${(props) => props.theme.backgroundColor || 'white'};
  -webkit-box-shadow: 1px 2px 14px -2px rgba(0, 0, 0, 0.25);
  box-shadow: 1px 2px 14px -2px rgba(0, 0, 0, 0.25);

  display: flex;
  flex-direction: column;
  font-weight: normal;
  & p {
    font-size: 1rem;
  }
`;

const BlockPreviewBox = styled(PreviewBox)`
  max-height: 250px;
`;

const TopDivider = styled.span`
  display: block;
  width: 100%;
  border-bottom: 1px solid black;
  padding: 5px;
`;

const JumpDownButton = styled.button`
  float: right;
  height: 20px;
  background-color: transparent;
  border: none;
  vertical-align: center;
`;

const GoToButton = styled.button`
  float: right;
  height: 20px;
  background-color: transparent;
  border: none;
  vertical-align: center;
`;

const PreviewHeader = styled.h3`
  display: inline-block;
  margin: 0px auto auto auto;
`;

const ContentWrapper = styled.span`
  display: block;
  height: 100%;
  padding: 10px;
  overflow-y: scroll;
`;
